import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useDispatch } from 'utils/redux';
import { changeLocale } from 'actions/env';
import { getStatsSharingTokenInfos } from 'actions/consent';

import Markdown from 'components/atoms/Markdown';
import Typography from 'components/atoms/Typography';
import BigFooter from 'components/templates/BigFooter';
import LinkInstagramButton from 'components/atoms/Buttons/LinkInstagramButton';

import Button from './components/Button';

import styles from './KolLandingPage.module.scss';

const browserLang = navigator.language || navigator.userLanguage;

const howToLinks = {
  en: {
    link: 'https://static.kolsquare.com/assets/kol_landing_page/InstaConnect-Process-EN.pdf',
    filename: 'InstaConnect-Process-EN.pdf',
  },
  fr: {
    link: 'https://static.kolsquare.com/assets/kol_landing_page/InstaConnect-Process-FR.pdf',
    filename: 'InstaConnect-Process-FR.pdf',
  },
};

const KolLandingPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { token } = useParams<{ token: string }>();

  const [companyName, setCompanyName] = useState('');

  const locale = browserLang?.split('-')[0] !== 'fr' ? 'en' : 'fr';

  useEffect(() => {
    dispatch(changeLocale(locale));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  useEffect(() => {
    if (token) {
      dispatch(getStatsSharingTokenInfos(token)).then((response) => {
        setCompanyName(response.salesforce_account_name);
      });
    }
  }, [dispatch, token]);

  return (
    <div className={styles.container}>
      <div className={styles.logo}>
        {/*  eslint-disable-next-line jsx-a11y/control-has-associated-label */}
        <a href="#advantages" target="_self" rel="noopener noreferrer">
          <span className={styles.imgPlaceholder} />
        </a>
        <div className={styles.menu}>
          <Typography variant="label/large" className={styles.descriptionText}>
            <a href="#advantages">
              <FormattedMessage id="KolLandingPage.menu.advantages" defaultMessage="Advantages" />
            </a>
          </Typography>
          <Typography variant="label/large" className={styles.descriptionText}>
            <a
              rel="noopener noreferrer"
              target="_blank"
              type="download"
              href={howToLinks[locale].link}
              download={howToLinks[locale].filename}
            >
              <FormattedMessage id="KolLandingPage.menu.howToDoIt" defaultMessage="How to do it?" />
            </a>
          </Typography>
          <Typography variant="label/large" className={styles.descriptionText}>
            <a href={`https://www.kolsquare.com/${locale}`} target="_blank" rel="noreferrer">
              <FormattedMessage id="KolLandingPage.menu.whoIsKolsquare" defaultMessage="Who is Kolsquare?" />
            </a>
          </Typography>
        </div>
      </div>
      <div className={styles.innerContainer}>
        <div className={styles.paddedTile}>
          <div className="flex gap42 aic jcsb">
            <div className="flex fdc gap32 f3">
              <div className="flex fdc gap22 aifs">
                <div className={styles.blueBgTitle}>
                  <FormattedMessage id="KolLandingPage.part1.title" defaultMessage="INSTA-CONNECT" />
                </div>
                <div className={styles.bigTitle}>
                  <FormattedMessage
                    id="KolLandingPage.part1.companyWantsAccess"
                    defaultMessage="{companyName} wants to access your Instagram statistics."
                    values={{ companyName }}
                  />
                </div>
                <Typography variant="text/body medium" className={styles.descriptionText}>
                  <FormattedMessage
                    id="KolLandingPage.part1.companyWantsAccess.message"
                    defaultMessage="{companyName} wants to learn more about your content. Log in to your statistics via Instaconnect to share them with brands."
                    values={{ companyName }}
                  />
                </Typography>
              </div>
              <div className={styles.topButtonContainer}>
                <LinkInstagramButton from="kol-landing-page" showTermsAndConditionsInsideModal />
                <a
                  type="download"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-nowrap"
                  href={howToLinks[locale].link}
                  download={howToLinks[locale].filename}
                >
                  <Button theme="secondary">
                    <FormattedMessage id="KolLandingPage.part1.cta.howTo" defaultMessage="How to" />
                  </Button>
                </a>
              </div>
            </div>

            <img
              srcSet="https://static.kolsquare.com/assets/kol_landing_page/part-1-no-brands.png 2x"
              alt="part-1-no-brands.png"
              className={styles.kolImage}
            />
          </div>
        </div>
        <div className={styles.paddedTile}>
          <div className={styles.centeredTitleTile}>
            <div className={`${styles.mediumTitle} tac`} id="advantages">
              <FormattedMessage
                id="KolLandingPage.part2.whyLinkMyInstagram"
                defaultMessage="Why Link My Instagram Account to Kolsquare?"
              />
            </div>
            <Typography variant="label/large" className={`${styles.descriptionText} tac`}>
              <FormattedMessage
                id="KolLandingPage.part2.message"
                defaultMessage="By connecting your Instagram account, you increase your chances of being noticed by leading brands such as:"
              />
            </Typography>
          </div>
        </div>
        <div className={styles.paddedTile}>
          <div className={styles.threeHorizontalTile}>
            {/* 1/3 */}
            <div className={styles.bgLightBlue}>
              <div className="flex fdc gap22 ais">
                <div />
                <img
                  srcSet="https://static.kolsquare.com/assets/kol_landing_page/part-1-brands-1.png 1.5x"
                  alt="part-1-brands-1.png"
                  className={styles.fullWidthImage}
                />
                <img
                  srcSet="https://static.kolsquare.com/assets/kol_landing_page/part-1-brands-2.png 1.5x"
                  alt="part-1-brands-2.png"
                  className={styles.fullWidthImage}
                />
                <div />
              </div>
              <div className={styles.innerTile}>
                <div className={styles.tinyTitle}>
                  <FormattedMessage
                    id="KolLandingPage.part3.workWithBestBrands"
                    defaultMessage="Work with the best brands."
                  />
                </div>
                <Typography variant="label/large" className={styles.descriptionText}>
                  <FormattedMessage
                    id="KolLandingPage.part3.workWithBestBrands.message"
                    defaultMessage="Top brands rely on Kolsquare to find and collaborate with the right creators. By linking your Instagram account, you make your real stats visible—boosting your chances of being selected for high-quality campaigns."
                  />
                </Typography>
              </div>
            </div>
            {/* 2/3 */}
            <div className={styles.bgDarkBlue}>
              <div>
                <img
                  srcSet={`https://static.kolsquare.com/assets/kol_landing_page/part-2-${locale}.png 1.5x`}
                  alt="part-1-brands-1.png"
                  className={styles.centeredImage}
                />
              </div>
              <div className={styles.innerTile}>
                <div className={styles.tinyTitle}>
                  <FormattedMessage
                    id="KolLandingPage.part3.facilitateYourExchanges"
                    defaultMessage="Facilitate your exchanges with your clients."
                  />
                </div>
                <Typography variant="label/large" className={styles.descriptionText}>
                  <FormattedMessage
                    id="KolLandingPage.part3.facilitateYourExchanges.message"
                    defaultMessage="Skip the screenshots and back-and-forth. With your account linked, brands can view your performance instantly, making communication quick and easy."
                  />
                </Typography>
              </div>
            </div>
            {/* 3/3 */}
            <div className={styles.bgLightBlue}>
              <div>
                <img
                  srcSet="https://static.kolsquare.com/assets/kol_landing_page/part-3.png 1.5x"
                  alt="part-1-brands-1.png"
                  className={styles.fixedRightImage}
                />
              </div>
              <div className={styles.innerTile}>
                <div className={styles.tinyTitle}>
                  <FormattedMessage
                    id="KolLandingPage.part3.increaseYourRevenue"
                    defaultMessage="Increase your revenue."
                  />
                </div>
                <Typography variant="label/large" className={styles.descriptionText}>
                  <FormattedMessage
                    id="KolLandingPage.part3.increaseYourRevenue.message"
                    defaultMessage="Linked stats make it easier for brands to see your value—so you can land better deals, secure more collaborations, and grow your income faster."
                  />
                </Typography>
              </div>
            </div>
          </div>
        </div>
        {/* Part 4 */}
        <div className={styles.bigBlueContainer}>
          {/* Title */}
          <div className={styles.centeredTitleTile}>
            <div className={`${styles.mediumTitle} tac`}>
              <FormattedMessage
                id="KolLandingPage.part4.howToLinkInstagram"
                defaultMessage="How to Link My Instagram Account?"
              />
            </div>
            <Typography variant="label/large" className={`${styles.descriptionText} tac`}>
              <Markdown>
                {intl.formatMessage({
                  id: 'KolLandingPage.part4.howToLinkInstagram.message',
                  defaultMessage:
                    'Before starting, make sure that: **Your Instagram account is a business account, Your Instagram account is linked to a Facebook Page, You are an admin of that Facebook Page.**',
                })}
              </Markdown>
            </Typography>
          </div>
          <div className={styles.columnContainer}>
            {/* Step 1 */}
            <div className={styles.wrappingContainer}>
              <div className={styles.imageContainer}>
                <img
                  srcSet={`https://static.kolsquare.com/assets/kol_landing_page/part-4-1-${locale}.png 1.5x`}
                  alt="part-4-1.png"
                />
              </div>
              <div className={styles.stepContainer}>
                <div className="flex fdc gap4">
                  <div className={styles.blueTitle}>
                    <FormattedMessage id="KolLandingPage.part4.step1" defaultMessage="Step 1" />
                  </div>
                  <div className={styles.smallTitle}>
                    <FormattedMessage
                      id="KolLandingPage.part4.step1.title"
                      defaultMessage="Login to your Facebook account"
                    />
                  </div>
                </div>
                <Typography variant="label/large" className={styles.descriptionText}>
                  <FormattedMessage
                    id="KolLandingPage.part4.step1.message"
                    defaultMessage="Please use the IDs of the Facebook account related to your Instagram account."
                  />
                </Typography>
                <LinkInstagramButton from="kol-landing-page" showTermsAndConditionsInsideModal />
              </div>
            </div>
            {/* Step 2 */}
            <div className={styles.wrappingContainer}>
              <div className={styles.imageContainer}>
                <img
                  srcSet={`https://static.kolsquare.com/assets/kol_landing_page/part-4-2-${locale}-new2.png 1.5x`}
                  alt="part-4-2.png"
                />
              </div>
              <div className={styles.stepContainer}>
                <div>
                  <div className={styles.blueTitle}>
                    <FormattedMessage id="KolLandingPage.part4.step2" defaultMessage="Step 2" />
                  </div>
                  <div className={styles.smallTitle}>
                    <FormattedMessage
                      id="KolLandingPage.part4.step2.title"
                      defaultMessage="Log or log back in your Facebook account to Community"
                    />
                  </div>
                </div>
                <Typography variant="label/large" className={styles.descriptionText}>
                  <FormattedMessage
                    id="KolLandingPage.part4.step2.message"
                    defaultMessage="Better click on CHANGE PREVIOUS SETTINGS to check if all is in order."
                  />
                </Typography>
              </div>
            </div>
            {/* Step 3 */}
            <div className={styles.wrappingContainer}>
              <div className={styles.imageContainer}>
                <img
                  srcSet={`https://static.kolsquare.com/assets/kol_landing_page/part-4-3-${locale}-new.png 1.5x`}
                  alt="part-4-3.png"
                />
              </div>
              <div className={styles.stepContainer}>
                <div>
                  <div className={styles.blueTitle}>
                    <FormattedMessage id="KolLandingPage.part4.step3" defaultMessage="Step 3" />
                  </div>
                  <div className={styles.smallTitle}>
                    <FormattedMessage
                      id="KolLandingPage.part4.step3.title"
                      defaultMessage="Choose the company Community can get access to"
                    />
                  </div>
                </div>
                <Typography variant="label/large" className={styles.descriptionText}>
                  <FormattedMessage
                    id="KolLandingPage.part4.step3.message"
                    defaultMessage="This company has to be an account related to your Facebook Business page and has to be visible and configured on Meta Business Suite."
                  />
                </Typography>
                <Typography variant="label/large" className={styles.descriptionText}>
                  <a
                    href="https://www.facebook.com/help/2978174759158711/?helpref=uf_share"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.link}
                  >
                    <FormattedMessage
                      id="KolLandingPage.part4.step3.link"
                      defaultMessage="Don't have a business account?"
                    />
                  </a>
                </Typography>
              </div>
            </div>
            {/* Step 4 */}
            <div className={styles.wrappingContainer}>
              <div className={styles.imageContainer}>
                <img
                  srcSet={`https://static.kolsquare.com/assets/kol_landing_page/part-4-4-${locale}-new.png 1.5x`}
                  alt="part-4-4.png"
                />
              </div>
              <div className={styles.stepContainer}>
                <div>
                  <div className={styles.blueTitle}>
                    <FormattedMessage id="KolLandingPage.part4.step4" defaultMessage="Step 4" />
                  </div>
                  <div className={styles.smallTitle}>
                    <FormattedMessage
                      id="KolLandingPage.part4.step4.title"
                      defaultMessage="Check pages Community can access to"
                    />
                  </div>
                </div>
                <Typography variant="label/large" className={styles.descriptionText}>
                  <FormattedMessage
                    id="KolLandingPage.part4.step4.message"
                    defaultMessage="Please select only one page to link with Community/Kolsquare"
                  />
                </Typography>
              </div>
            </div>
            {/* Step 5 */}
            <div className={styles.wrappingContainer}>
              <div className={styles.imageContainer}>
                <img
                  srcSet={`https://static.kolsquare.com/assets/kol_landing_page/part-4-5-${locale}.png 1.5x`}
                  alt="part-4-5.png"
                />
              </div>
              <div className={styles.stepContainer}>
                <div>
                  <div className={styles.blueTitle}>
                    <FormattedMessage id="KolLandingPage.part4.step5" defaultMessage="Step 5" />
                  </div>
                  <div className={styles.smallTitle}>
                    <FormattedMessage
                      id="KolLandingPage.part4.step5.title"
                      defaultMessage="Check the Instagram account related to this Facebook Business Page"
                    />
                  </div>
                </div>
                <Typography variant="label/large" className={styles.descriptionText}>
                  <FormattedMessage
                    id="KolLandingPage.part4.step5.message"
                    defaultMessage="Please select only one account. Plus, it is essential that only one account is related to only one page. You can un-relate other accounts from a Facebook page directly in Business Suite."
                  />
                </Typography>
              </div>
            </div>
            {/* Step 6 */}
            <div className={styles.wrappingContainer}>
              <div className={styles.imageContainer}>
                <img
                  srcSet={`https://static.kolsquare.com/assets/kol_landing_page/part-4-6-${locale}.png 1.5x`}
                  alt="part-4-6.png"
                />
              </div>
              <div className={styles.stepContainer}>
                <div>
                  <div className={styles.blueTitle}>
                    <FormattedMessage id="KolLandingPage.part4.step6" defaultMessage="Step 6" />
                  </div>
                  <div className={styles.smallTitle}>
                    <FormattedMessage
                      id="KolLandingPage.part4.step6.title"
                      defaultMessage="Check Community and Meta authorizations"
                    />
                  </div>
                </div>
                <Typography variant="label/large" className={styles.descriptionText}>
                  <FormattedMessage
                    id="KolLandingPage.part4.step6.message"
                    defaultMessage="Check you have ALL ACCESSES listed on this screenshot"
                  />
                </Typography>
              </div>
            </div>
            <div className={styles.buttonContainer}>
              <LinkInstagramButton from="kol-landing-page" showTermsAndConditionsInsideModal />
              <a
                type="download"
                target="_blank"
                rel="noopener noreferrer"
                href={howToLinks[locale].link}
                download={howToLinks[locale].filename}
              >
                <Button theme="secondary">
                  <FormattedMessage id="KolLandingPage.part4.cta.downloadPdf" defaultMessage="Download PDF" />
                </Button>
              </a>
            </div>
          </div>
        </div>
        {/* Part 5: REMOVED */}
        {/* Part 6 */}
        <div className={`${styles.bgDarkBlue} ${styles.rowIfDesktop}`}>
          <div className={styles.leftImage}>
            <img src="https://static.kolsquare.com/assets/kol_landing_page/part-6-planet.svg" alt="part-6-planet.svg" />
          </div>
          <div className={styles.innerTile}>
            <div className={`${styles.tinyTitle} ${styles.smaller}`}>
              <FormattedMessage id="KolLandingPage.part6.yourDataIsSafe" defaultMessage="Your Data Is Safe" />
            </div>
            <Typography variant="label/large" className={styles.descriptionText}>
              <FormattedMessage
                id="KolLandingPage.part6.yourDataIsSafe.message"
                defaultMessage="We only collect your Instagram performance data to share your performance stats (Reels, posts, stories) with brands for potential collaborations.
Brands can see whether your account is linked, increasing your chances of being selected."
              />
            </Typography>
          </div>
        </div>
      </div>
      <BigFooter className={styles.bigFooter} />
    </div>
  );
};

export default KolLandingPage;
